"use client"

import type { ReactNode } from "react"
import {
  ArrowRight,
  BarChart3,
  Check,
  Cog,
  Megaphone,
  MessageCircle,
  ShoppingBag,
  Sparkles,
  Target,
  type LucideIcon,
} from "lucide-react"
import { indexVar } from "@/components/marketing/landing-v2/cssVars"
import { PanelBar } from "@/components/marketing/landing-v2/PanelBar"
import { useReveal } from "@/components/marketing/landing-v2/useReveal"
import {
  CATEGORY_ORDER,
  type Landing2Stage,
  type LandingCategoryId,
} from "@/lib/landing-v2/content"
import { formatCopy, type Landing2Copy } from "@/lib/landing-v2/copy"

const CATEGORY_ICONS: Record<LandingCategoryId, LucideIcon> = {
  ecommerce: ShoppingBag,
  customer_support: MessageCircle,
  sales: Target,
  data_reporting: BarChart3,
  marketing: Megaphone,
  operations: Cog,
  other: Sparkles,
}

/** Wraps the emphasised phrase so the headline keeps one accented span. */
export function withEmphasis(text: string, emphasis: string): ReactNode {
  const start = text.indexOf(emphasis)
  if (start === -1) return text

  return (
    <>
      {text.slice(0, start)}
      <em>{emphasis}</em>
      {text.slice(start + emphasis.length)}
    </>
  )
}

type DiscoveryPanelProps = {
  copy: Landing2Copy
  stage: Landing2Stage
  selected: LandingCategoryId[]
  onToggle: (categoryId: LandingCategoryId) => void
  otherText: string
  onOtherTextChange: (value: string) => void
  onContinue: () => void
}

export function DiscoveryPanel({
  copy,
  stage,
  selected,
  onToggle,
  otherText,
  onOtherTextChange,
  onContinue,
}: DiscoveryPanelProps) {
  const ref = useReveal<HTMLDivElement>()

  return (
    <section className="l2-section l2-section-discovery" id="discovery">
      <div className="l2-container">
        <div className="l2-panel" ref={ref}>
          <PanelBar
            label={copy.discovery.label}
            stage={stage}
            stageNames={copy.discovery.stages}
          />

          <div className="l2-panel-body">
            <div className="l2-panel-head">
              <h2 className="l2-h2">
                {withEmphasis(copy.discovery.title, copy.discovery.titleEmphasis)}
              </h2>
              <p className="l2-panel-sub">{copy.discovery.subtitle}</p>
            </div>

            <div className="l2-category-grid">
              {CATEGORY_ORDER.map((categoryId, index) => {
                const Icon = CATEGORY_ICONS[categoryId]
                const isSelected = selected.includes(categoryId)
                const category = copy.categories[categoryId]

                return (
                  <button
                    key={categoryId}
                    type="button"
                    className="l2-category"
                    data-selected={isSelected || undefined}
                    data-wide={categoryId === "other" || undefined}
                    style={indexVar(index)}
                    aria-pressed={isSelected}
                    onClick={() => onToggle(categoryId)}
                  >
                    <span className="l2-category-icon">
                      <Icon size={18} strokeWidth={1.75} />
                    </span>
                    <span className="l2-category-text">
                      <strong>{category.label}</strong>
                      <small>{category.hint}</small>
                    </span>
                    <span className="l2-category-check" aria-hidden="true">
                      <Check size={12} strokeWidth={3.2} />
                    </span>
                  </button>
                )
              })}
            </div>

            {selected.includes("other") && (
              <div className="l2-inline-field">
                <textarea
                  className="l2-input l2-textarea"
                  value={otherText}
                  onChange={(event) => onOtherTextChange(event.target.value)}
                  placeholder={copy.categoryOtherPlaceholder}
                  aria-label={copy.categoryOtherPlaceholder}
                  rows={2}
                />
              </div>
            )}

            <div className="l2-panel-foot">
              <button
                type="button"
                className="l2-button"
                disabled={selected.length === 0}
                onClick={onContinue}
              >
                {copy.discovery.continue}
                <ArrowRight size={15} strokeWidth={2.2} />
              </button>
              {selected.length > 0 && (
                <span className="l2-meta">
                  {formatCopy(copy.discovery.selectedCount, { count: selected.length })}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
